import posthog from 'posthog-js';
import { Sentry } from './sentry';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

// Determine environment: use VITE_APP_ENV if set, otherwise fall back to MODE
// This allows staging and production to be properly distinguished
const getEnvironment = () => {
  const appEnv = import.meta.env.VITE_APP_ENV;
  if (appEnv) return appEnv;

  // Fallback to MODE for local development
  const mode = import.meta.env.MODE;
  return mode === 'development' ? 'development' : 'production';
};

export const initPostHog = () => {
  if (POSTHOG_KEY) {
    const environment = getEnvironment();

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only',

      // Page tracking
      capture_pageview: true,
      capture_pageleave: true,

      // Autocapture settings
      autocapture: {
        // Capture clicks, form submissions, etc.
        capture_copied_text: false, // Don't capture copied text (privacy)
        url_allowlist: environment === 'production' ? undefined : ['localhost', 'vercel.app'], // Only in dev/staging
      },

      // Disable verbose console logging
      disable_external_dependency_loading: false,

      // Reduce console noise - only log in development
      debug: environment === 'development',

      // Session recording settings
      // MVP: Higher sampling for better user journey visibility
      session_recording: {
        // Only log session recording errors, not every capture
        console_log_recording_enabled: false,
        // Mask sensitive inputs
        maskTextSelector: '[data-private], input[type="password"], input[type="email"]',
        maskAllInputs: false,
        // MVP-optimized sample rates
        sampleRate: environment === 'production' ? 0.25 : 1.0, // 25% prod, 100% dev/staging
      },

      // Enable in all environments for MVP visibility
      disable_session_recording: false,

      // Respect user privacy
      respect_dnt: true, // Respect Do Not Track browser setting
      opt_out_capturing_by_default: false,

      // Set environment for filtering in PostHog
      bootstrap: {
        distinctID: undefined,
      },

      loaded: (posthog) => {
        // Set environment as a super property so it's included with all events
        posthog.register({
          environment: environment,
        });

        // Link PostHog sessions with Sentry errors
        const distinctId = posthog.get_distinct_id();
        if (distinctId && Sentry) {
          Sentry.setUser({ id: distinctId });
          Sentry.setTag('posthog_distinct_id', distinctId);
        }
      },
    });

    console.log(`PostHog initialized for ${environment} environment`);
  } else {
    console.warn('PostHog key not found. Analytics will be disabled.');
  }
};

export { posthog };
